import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space, Popconfirm, Input as AntdInput, Checkbox } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_BASE_URL } from '../constants/api';
import CategoryCreateModal from './CategoryCreateModal';
import MaterialCreateModal from './MaterialCreateModal';
import MaterialCard from './MaterialCard';
import SearchBar from './common/SearchBar';
import { useDebounce } from 'use-debounce';

const { Option } = Select;

const MaterialManagement = () => {
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [form] = Form.useForm();
  const [isCategoryModalVisible, setIsCategoryModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm] = Form.useForm();
  const [materialForm] = Form.useForm();
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [isCardVisible, setIsCardVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);

  useEffect(() => {
    fetchMaterials();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (debouncedSearchTerm) {
      fetchMaterials(debouncedSearchTerm);
    } else {
      fetchMaterials();
    }
  }, [debouncedSearchTerm]);

  const fetchMaterials = async (query = '') => {
    setSearchLoading(true);
    try {
      const params = query ? { Name: query } : {};
      const response = await axios.get(`${API_BASE_URL}Material/List`, { params });
      if (response.data.code === 0) {
        setMaterials(response.data.data || []);
      } else {
        message.error(response.data.message || 'فشل في جلب المواد');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'خطأ في جلب المواد';
      message.error(errorMessage);
    }
    setSearchLoading(false);
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}Category/List`);
      if (response.data.code === 0) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching categories');
    }
  };

  const handleCreate = () => {
    setEditingMaterial(null);
    materialForm.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingMaterial(record);
    materialForm.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}Material/Delete/${id}`);
      if (response.data.code === 0) {
        message.success(response.data.message || 'تم حذف المادة بنجاح');
        fetchMaterials();
      } else {
        message.error(response.data.message || 'فشل في حذف المادة');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'خطأ في حذف المادة';
      message.error(errorMessage);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingMaterial && editingMaterial.id) {
        const response = await axios.put(`${API_BASE_URL}Material/Update/${editingMaterial.id}`, values);
        if (response.data.code === 0) {
          message.success('تم تحديث المادة بنجاح');
          setIsModalVisible(false);
          fetchMaterials();
        } else {
          message.error(response.data.message || 'فشل في تحديث المادة');
        }
      } else {
        const response = await axios.post(`${API_BASE_URL}Material/Create`, values);
        if (response.data.code === 0) {
          message.success('تم إنشاء المادة بنجاح');
          setIsModalVisible(false);
          fetchMaterials();
        } else {
          message.error(response.data.message || 'فشل في إنشاء المادة');
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'خطأ في حفظ المادة';
      message.error(errorMessage);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const handleMaterialSuccess = () => {
    fetchMaterials();
  };

  const handleCategoryCreate = () => {
    setEditingCategory(null);
    categoryForm.resetFields();
    setIsCategoryModalVisible(true);
  };

  const handleCategoryModalCancel = () => {
    setIsCategoryModalVisible(false);
  };

  const handleCategoryModalSuccess = () => {
    fetchCategories();
    setIsCategoryModalVisible(false);
  };

  const columns = [
    {
      title: 'معرف',
      dataIndex: 'id',
      key: 'Id',
    },
    {
      title: 'الاسم',
      dataIndex: 'name',
      key: 'Name',
      render: (name, record) => (
        <a 
          href="#" 
          onClick={(e) => {
            e.preventDefault();
            setSelectedMaterial(record);
            setIsCardVisible(true);
          }}
          style={{ color: '#191c1f', textDecoration: 'none' }}
        >
          {name}
        </a>
      ),
    },

{
      title: 'الفئة',
      dataIndex: 'categoryName',
      key: 'Category',
      render: (category) => category ?? '',
    },
    {
      title: 'الرصيد',
      dataIndex: 'stockAmount',
      key: 'StockAmount',
      render: (value) => (value ?? 0).toFixed(2),
    },
    {
      title: 'سالب الرصيد',
      dataIndex: 'allowNegativeBalance',
      key: 'AllowNegativeBalance',
      render: (value) => value ? 'نعم' : 'لا',
    },
    {
      title: 'الوحدة',
      dataIndex: 'unit',
      key: 'Unit',
      render: (unit) => unit || '',
    },
    {
      title: 'الإجراءات',
      key: 'actions',
      render: (text, record) => (
        <Space size="middle">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title="هل أنت متأكد من حذف هذه المادة؟"
            onConfirm={() => handleDelete(record.id)}
            okText="نعم"
            cancelText="لا"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          إضافة مادة
        </Button>
        <SearchBar
          onSearch={setSearchTerm}
          loading={searchLoading}
          placeholder="ابحث بالاسم..."
        />
      </div>
      <div className="table-responsive-wrapper">
        <Table
          columns={columns}
          dataSource={materials}
          rowKey="id"
          loading={loading || searchLoading}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: searchTerm ? 'لا توجد مواد بهذا الاسم' : 'لا توجد مواد' }}
        />
      </div>
      <MaterialCreateModal
        open={isModalVisible}
        onCancel={handleModalCancel}
        onSuccess={handleMaterialSuccess}
        form={materialForm}
        editingMaterial={editingMaterial}
        categories={categories}
      />
      <Modal
        open={isCardVisible}
        onCancel={() => setIsCardVisible(false)}
        footer={null}
        width={600}
        title="تفاصيل المادة"
      >
        <MaterialCard
          material={selectedMaterial}
        />

      </Modal>
      <CategoryCreateModal
        open={isCategoryModalVisible}
        onCancel={handleCategoryModalCancel}
        onSuccess={handleCategoryModalSuccess}
        form={categoryForm}
        categories={categories}
        editingCategory={editingCategory}
      /> 
    </div>
  );
};

export default MaterialManagement;
